import { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar as AvatarType, UserFamilyMemberType } from 'src/types/user.types';
import {
  BoyAvatar,
  CatAvatar,
  DogAvatar,
  GirlAvatar,
  NeutralAvatar,
  UnicornAvatar,
} from 'src/components/Avatar/avatarImages';
import Skeleton from 'src/designSystem/Skeleton';
import { twMerge } from 'src/utils/twMerge';

const avatarIconMap = {
  BOY: BoyAvatar,
  GIRL: GirlAvatar,
  NEUTRAL: NeutralAvatar,
  CAT: CatAvatar,
  DOG: DogAvatar,
  UNICORN: UnicornAvatar,
};

type Props = {
  avatar?: AvatarType;
  familyMemberType?: UserFamilyMemberType | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  sizeClassName?: string;
  className?: string;
  isSkeletonShown?: boolean;
};

const sizeClasses = {
  sm: 'w-auto h-8 max-h-full',
  md: 'w-auto h-16 max-h-full',
  lg: 'w-auto h-32 max-h-full',
  xl: 'w-auto h-48 max-h-full',
};

const defaultAvatarByGender = {
  [UserFamilyMemberType.boy]: BoyAvatar,
  [UserFamilyMemberType.girl]: GirlAvatar,
  [UserFamilyMemberType.other]: NeutralAvatar,
  [UserFamilyMemberType.mother]: NeutralAvatar,
  [UserFamilyMemberType.father]: NeutralAvatar,
};

const Avatar: FunctionComponent<Props> = ({
  avatar,
  familyMemberType,
  size = 'md',
  sizeClassName,
  className = '',
  isSkeletonShown = false,
}) => {
  const { t } = useTranslation();
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const avatarSize = sizeClassName ?? sizeClasses[size];
  const fadeInClassName = 'motion-safe:animate-skeleton-fade-in';
  const withSkeleton = (key: string, image: ReactNode) => (
    <div
      className={twMerge(
        'flex items-center justify-center',
        isSkeletonShown && 'relative',
        className,
      )}
    >
      {image}
      {isSkeletonShown && loadedUrl !== key ? (
        <Skeleton
          className="absolute inset-0"
          data-testid="avatar-image-skeleton"
        />
      ) : null}
    </div>
  );
  const imageClassName = (key: string) =>
    twMerge(
      avatarSize,
      isSkeletonShown && loadedUrl === key && fadeInClassName,
    );

  if (avatar?.url) {
    const url = avatar.url;
    return withSkeleton(
      url,
      <img
        src={url}
        alt={t('global.alt.userAvatar')}
        className={twMerge('object-contain', imageClassName(url))}
        onLoad={() => setLoadedUrl(url)}
        onError={() => setLoadedUrl(url)}
      />,
    );
  }

  if (
    avatar?.uuid &&
    avatarIconMap[avatar.type as keyof typeof avatarIconMap]
  ) {
    const AvatarIcon = avatarIconMap[avatar.type as keyof typeof avatarIconMap];
    const key = `icon-${avatar.type}`;
    return withSkeleton(
      key,
      <AvatarIcon
        className={imageClassName(key)}
        onLoaded={() => setLoadedUrl(key)}
      />,
    );
  }

  const DefaultIcon =
    (familyMemberType ? defaultAvatarByGender[familyMemberType] : undefined) ??
    NeutralAvatar;
  const key = `default-${familyMemberType ?? 'neutral'}`;
  return withSkeleton(
    key,
    <DefaultIcon
      className={imageClassName(key)}
      onLoaded={() => setLoadedUrl(key)}
      data-testid={`avatar-default-${familyMemberType ?? 'neutral'}`}
    />,
  );
};

export default Avatar;
